import Phaser from 'phaser';
import { SCENES, DESIGN_WIDTH, DESIGN_HEIGHT, COLORS, COLORS_HEX, DEPTH } from '../constants';
import { SceneRouter } from '../systems/SceneRouter';
import { InputRouter } from '../systems/InputRouter';
import { AudioRouter } from '../systems/AudioRouter';
import { LevelLoader } from '../systems/LevelLoader';
import { ProgressionSystem } from '../systems/ProgressionSystem';
import { ProgressState } from '../state/ProgressState';
import { GameState } from '../state/GameState';
import { UIButton } from '../ui/UIButton';
import { UIPanel } from '../ui/UIPanel';
import { PlaceholderFactory } from '../placeholder/PlaceholderFactory';
import { formatNumber, formatTime } from '../../shared/utils';
import type { TowerDamageStats } from '../systems/LevelStatsTracker';
import { AuthService } from '../systems/AuthService';
import { AuthUI } from '../systems/AuthUI';
import { fetchLevelScores } from '../systems/LevelScoresApi';

// ============================================
// MapScene (Step 12 Integration)
// ============================================

interface MapNode {
  id: string;
  scenarioId: string;
  x: number;
  y: number;
  name: string;
  description: string;
  difficulty: number;
  region: number;
  isChallenge: boolean;
  isGate: boolean;
  unlocked: boolean;
  completed: boolean;
  stars: number;
  bestScore: number;
  bestTime: number;
  bestPlayerCount: number;
  container: Phaser.GameObjects.Container;
  connections: string[]; // IDs of connected nodes
}

// Region colors
const REGION_COLORS: Record<number, number> = {
  1: 0x228b22, // Forest - green
  2: 0x556b2f, // Swamp - dark olive
  3: 0x8b7355, // Ruins - tan/brown
  4: 0xcc3300, // Volcano - red-orange
};

const REGION_NAMES = ['', 'Forest Edge', 'Swamp & River', 'Ruins & Highlands', 'Volcano Core'];

// Map layout: 2x2 grid of regions, centered on screen
const MAP_REGION_WIDTH = 500;
const MAP_REGION_HEIGHT = 280;
const MAP_LEFT = DESIGN_WIDTH / 2 - MAP_REGION_WIDTH;   // center the 2-column grid
const MAP_TOP = DESIGN_HEIGHT / 2 - MAP_REGION_HEIGHT;   // center the 2-row grid

// Chase animation (same as MenuScene)
const CHASE_PLAYER_NUMBERS = [1, 2, 3, 4];
function getChasePlayerRunKeys(playerNum: number): string[] {
  return [`player-${playerNum}-run-1`, `player-${playerNum}-run-2`, `player-${playerNum}-run-3`, `player-${playerNum}-run-4`];
}
const CHASE_MONSTER_TYPES: Array<{ type: string; frameCount: number }> = [
  { type: 'crawler', frameCount: 2 }, { type: 'runner', frameCount: 2 }, { type: 'brute', frameCount: 4 },
  { type: 'shielded', frameCount: 3 }, { type: 'bat', frameCount: 2 }, { type: 'flyer', frameCount: 3 },
  { type: 'juggernaut', frameCount: 4 }, { type: 'swarm', frameCount: 3 },
];
const CHASE_Y_MAP = 860;
const CHASE_DURATION_MS = 4160;    // 30% slower than 3200
const CHASE_FIRST_DELAY_MS = 15000;
const CHASE_INTERVAL_MS = 30000;
const CHASE_PLAYER_FRAME_MS = 75; // 4 run frames, half of previous 2-frame interval
const CHASE_MONSTER_FRAME_MS = 200;
const CHASE_PLAYER_SCALE = 0.294;  // 30% smaller than 0.42
const CHASE_MONSTER_SCALE = 0.5;
const CHASE_MONSTER_START_OFFSET = 1520; // Doubled again
const CHASE_MONSTER_DURATION_MULT = 1.02; // Monster slightly slower (never catches up)
const CHASE_OFFSCREEN_MARGIN = 180;
const CHASE_DEPTH_BEHIND_UI = 0; // Behind map panel (5) and text (10); buttons use 1210

// Focus indices: 0..nodes.length-1 = map nodes; then Tutorial, Experiment, Map Editor, Start, Back
const FOCUS_TUTORIAL = 0;
const FOCUS_EXPERIMENT = 1;
const FOCUS_MAP_EDITOR = 2;
const FOCUS_START = 3;
const FOCUS_BACK = 4;

export class MapScene extends Phaser.Scene {
  private nodes: MapNode[] = [];
  private selectedIndex: number = 0;
  private startButton!: UIButton;
  private backButton!: UIButton;
  private tutorialButton!: UIButton;
  private experimentButton!: UIButton;
  private mapEditorButton!: UIButton;
  private infoPanel!: UIPanel;
  private lastNodeIndex: number = 0;

  // Map selection: dancing character (randomly one of 4 players), BPM-synced
  private mapSelectorDancer: Phaser.GameObjects.Image | null = null;
  private mapDanceKeys: string[] = [];
  private mapDanceFrameIndex: number = 0;
  private mapDanceTimer: number = 0;
  private static readonly MAP_DANCE_FRAMES_PER_CYCLE = 4;
  private static readonly MAP_DANCE_CYCLES_BEFORE_FLIP = 4;
  private mapDanceCycleCount: number = 0;
  private mapDanceFlipped: boolean = false;
  private static readonly MAP_SELECTOR_SCALE = 0.245; // 30% smaller than 0.35

  // Chase animation state
  private chaseNextDirection: number = 1;
  private chasePlayerSprite: Phaser.GameObjects.Image | null = null;
  private chaseMonsterSprite: Phaser.GameObjects.Image | null = null;
  private chaseMonsterType: string = '';
  private chaseMonsterFrameCount: number = 2;
  private chasePlayerRunKeys: string[] = getChasePlayerRunKeys(1);
  private chasePlayerFrameTimer: number = 0;
  private chaseMonsterFrameTimer: number = 0;
  private chasePlayerFrameIndex: number = 0;
  private chaseMonsterFrameIndex: number = 0;

  // Account button (top-right)
  private accountContainer?: Phaser.GameObjects.Container;
  private accountProfileImage?: Phaser.GameObjects.Image;
  private accountText?: Phaser.GameObjects.Text;
  private authUnsubscribe?: () => void;
  private loginDancerSprite: Phaser.GameObjects.Image | null = null;
  private loginDancerFrameIndex: number = 0;
  private loginDancerTimer: number = 0;
  private loginDancerCycleCount: number = 0;
  private loginDancerFlipped: boolean = false;
  private static readonly LOGIN_DANCE_FRAMES_PER_CYCLE = 4;
  private static readonly LOGIN_DANCE_CYCLES_BEFORE_FLIP = 4;

  // Info panel elements
  private levelNameText!: Phaser.GameObjects.Text;
  private levelDescText!: Phaser.GameObjects.Text;
  private levelDifficultyText!: Phaser.GameObjects.Text;
  private levelStarsText!: Phaser.GameObjects.Text;
  private levelScoreText!: Phaser.GameObjects.Text;
  private levelTimeText!: Phaser.GameObjects.Text;
  private levelGlobalBestText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private towerStatsText!: Phaser.GameObjects.Text;
  private _lastFetchedScenarioId: string = '';

  constructor() {
    super({ key: SCENES.MAP });
  }

  create(): void {
    console.log('[MapScene] Showing world map...');
    // Clear experiment mode whenever we're on the map so normal levels don't inherit it
    GameState.endExperimentMode();
    SceneRouter.setCurrentScene(SCENES.MAP);
    InputRouter.init(this);
    InputRouter.setMenuMode(true); // Higher deadzone for menu navigation

    AudioRouter.startMenuMusic(this);

    // Menu background (behind everything, including chase animation)
    if (this.textures.exists('menu-background')) {
      const bg = this.add.image(DESIGN_WIDTH / 2, DESIGN_HEIGHT / 2, 'menu-background');
      bg.setDisplaySize(DESIGN_WIDTH, DESIGN_HEIGHT);
      bg.setOrigin(0.5);
      bg.setDepth(-1);
    }

    // Title (Tiki Gold - main UI)
    this.add.text(DESIGN_WIDTH / 2, 40, 'WORLD MAP', {
      fontSize: '32px',
      color: COLORS_HEX.TIKI_GOLD,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    // Progress indicator (Tiki Gold - currency/stars)
    const progress = ProgressionSystem.getTotalStars();
    this.add.text(DESIGN_WIDTH / 2, 80, `★ ${progress.earned} / ${progress.possible}`, {
      fontSize: '18px',
      color: COLORS_HEX.TIKI_GOLD,
    }).setOrigin(0.5);

    // Create map nodes
    this.createMapNodes();

    // Create info panel
    this.createInfoPanel();

    // Create navigation buttons
    this.createButtons();

    // Chase animation (player runs across chased by monster)
    this.setupChaseAnimation();

    // Select first unlocked node or first node
    this.selectFirstAvailable();

    // Create selection dancer after we have a selected node (so it appears when returning to map)
    this.createMapSelectorDancer();

    // Account button (top-right)
    this.createAccountButton();

    this.updateSelection();
  }

  shutdown(): void {
    this.mapSelectorDancer = null;
    this.chasePlayerSprite = null;
    this.chaseMonsterSprite = null;
    this.loginDancerSprite = null;
    if (this.authUnsubscribe) {
      this.authUnsubscribe();
      this.authUnsubscribe = undefined;
    }
  }

  private pickRandomMapDancerKeys(): string[] {
    const keysFor = (n: number) => [
      `player-${n}-dance-1`,
      `player-${n}-dance-2`,
      `player-${n}-dance-3`,
      `player-${n}-dance-4`,
    ];
    const hasAll = (n: number) => keysFor(n).every((k) => this.textures.exists(k));
    const available = [1, 2, 3, 4].filter(hasAll);
    if (available.length === 0) {
      if (this.textures.exists('player-1-dance-1')) {
        return keysFor(1);
      }
      return [];
    }
    const chosen = available[Math.floor(Math.random() * available.length)];
    return keysFor(chosen);
  }

  private createMapSelectorDancer(): void {
    if (this.mapSelectorDancer && this.mapSelectorDancer.scene === this) {
      this.mapSelectorDancer.destroy();
    }
    this.mapSelectorDancer = null;

    this.mapDanceKeys = this.pickRandomMapDancerKeys();
    if (this.mapDanceKeys.length === 0) return;

    const node = this.nodes[this.selectedIndex] ?? this.nodes[0];
    if (!node) return;

    this.mapSelectorDancer = this.add.image(node.x, node.y - 18, this.mapDanceKeys[0]);
    this.mapSelectorDancer.setScale(MapScene.MAP_SELECTOR_SCALE);
    this.mapSelectorDancer.setDepth(15);
    this.mapDanceFrameIndex = 0;
    this.mapDanceTimer = 0;
    this.mapDanceCycleCount = 0;
    this.mapDanceFlipped = false;
  }

  private setupChaseAnimation(): void {
    this.time.delayedCall(CHASE_FIRST_DELAY_MS, () => this.runChaseAnimation());
  }

  private runChaseAnimation(): void {
    const rightToLeft = this.chaseNextDirection === 1;
    this.chaseNextDirection *= -1;

    const margin = CHASE_OFFSCREEN_MARGIN;
    const startX = rightToLeft ? DESIGN_WIDTH + margin : -margin;
    const endX = rightToLeft ? -margin : DESIGN_WIDTH + margin;
    const monsterOffset = rightToLeft ? CHASE_MONSTER_START_OFFSET : -CHASE_MONSTER_START_OFFSET;
    const monsterStartX = startX + monsterOffset;
    const monsterEndX = endX; // Same as player so both go fully off screen

    const available = CHASE_MONSTER_TYPES.filter(
      (m) => this.textures.exists(`monster-${m.type}-1`)
    );
    const chosen = available.length > 0
      ? available[Math.floor(Math.random() * available.length)]
      : CHASE_MONSTER_TYPES[0];
    this.chaseMonsterType = chosen.type;
    this.chaseMonsterFrameCount = chosen.frameCount;

    // Pick a random player (1–4) for this chase; fallback to player 1 if textures missing
    const shuffled = [...CHASE_PLAYER_NUMBERS].sort(() => Math.random() - 0.5);
    let runKeys = getChasePlayerRunKeys(1);
    for (const n of shuffled) {
      const keys = getChasePlayerRunKeys(n);
      if (this.textures.exists(keys[0])) {
        runKeys = keys;
        break;
      }
    }
    this.chasePlayerRunKeys = runKeys;

    const playerKey = this.chasePlayerRunKeys[0];
    if (!this.textures.exists(playerKey)) return;
    this.chasePlayerSprite = this.add.image(startX, CHASE_Y_MAP, playerKey);
    this.chasePlayerSprite.setScale(CHASE_PLAYER_SCALE);
    this.chasePlayerSprite.setFlipX(rightToLeft);
    this.chasePlayerSprite.setDepth(CHASE_DEPTH_BEHIND_UI);
    this.chasePlayerFrameTimer = 0;
    this.chasePlayerFrameIndex = 0;

    const monsterKey = `monster-${this.chaseMonsterType}-1`;
    if (!this.textures.exists(monsterKey)) {
      this.chasePlayerSprite.destroy();
      this.chasePlayerSprite = null;
      this.scheduleNextChase();
      return;
    }
    this.chaseMonsterSprite = this.add.image(monsterStartX, CHASE_Y_MAP, monsterKey);
    this.chaseMonsterSprite.setScale(CHASE_MONSTER_SCALE);
    this.chaseMonsterSprite.setFlipX(rightToLeft);
    this.chaseMonsterSprite.setDepth(CHASE_DEPTH_BEHIND_UI);
    this.chaseMonsterFrameTimer = 0;
    this.chaseMonsterFrameIndex = 0;

    const duration = CHASE_DURATION_MS;
    this.tweens.add({
      targets: this.chasePlayerSprite,
      x: endX,
      duration,
      ease: 'Linear',
      onComplete: () => {
        this.chasePlayerSprite?.destroy();
        this.chasePlayerSprite = null;
        this.chaseMonsterSprite?.destroy();
        this.chaseMonsterSprite = null;
        this.scheduleNextChase();
      },
    });
    this.tweens.add({
      targets: this.chaseMonsterSprite,
      x: monsterEndX,
      duration: duration * CHASE_MONSTER_DURATION_MULT,
      ease: 'Linear',
    });
  }

  private scheduleNextChase(): void {
    this.time.delayedCall(CHASE_INTERVAL_MS, () => this.runChaseAnimation());
  }

  private createMapNodes(): void {
    const scenarios = ProgressionSystem.getAllScenarioStatus();
    const nodePositions = this.calculateNodePositions();

    this.nodes = [];

    scenarios.forEach((status) => {
      const pos = nodePositions[status.id];
      if (!pos) return; // Skip if position not defined

      const def = LevelLoader.getScenarioDef(status.id);
      if (!def) return;

      const node: MapNode = {
        id: status.id,
        scenarioId: status.id,
        x: pos.x,
        y: pos.y,
        name: def.name ?? status.id,
        description: def.description ?? '',
        difficulty: def.difficulty ?? 1,
        region: def.regionNumber ?? 1,
        isChallenge: def.isChallenge ?? false,
        isGate: def.isGate ?? false,
        unlocked: status.unlocked,
        completed: status.completed,
        stars: status.stars,
        bestScore: status.bestScore,
        bestTime: status.bestTime,
        bestPlayerCount: status.bestPlayerCount ?? 1,
        container: this.createNodeVisual(pos.x, pos.y, status, def),
        connections: def.unlocks ?? [],
      };

      this.nodes.push(node);
    });

    // Draw connections between nodes
    this.drawConnections();
    
    // Draw region labels
    this.drawRegionLabels();
  }

  private calculateNodePositions(): Record<string, { x: number; y: number }> {
    // Spread out layout - 2x2 grid of regions (centered via MAP_LEFT, MAP_TOP)
    const positions: Record<string, { x: number; y: number }> = {};
    
    const mapLeft = MAP_LEFT;
    const mapTop = MAP_TOP;
    const nodeSpacingX = 120;
    const regionWidth = MAP_REGION_WIDTH;
    const regionHeight = MAP_REGION_HEIGHT;
    
    // Region 1 - Forest Edge (Top Left)
    const r1X = mapLeft;
    const r1Y = mapTop;
    positions['F1'] = { x: r1X + 80, y: r1Y + 120 };
    positions['F2'] = { x: r1X + 80 + nodeSpacingX, y: r1Y + 80 };
    positions['F2C'] = { x: r1X + 80 + nodeSpacingX, y: r1Y + 180 }; // Challenge below
    positions['F3'] = { x: r1X + 80 + nodeSpacingX * 2, y: r1Y + 100 };
    positions['F4'] = { x: r1X + 80 + nodeSpacingX * 3, y: r1Y + 140 }; // Gate
    
    // Region 2 - Swamp (Top Right)
    const r2X = mapLeft + regionWidth;
    const r2Y = mapTop;
    positions['S1'] = { x: r2X + 80, y: r2Y + 120 };
    positions['S2'] = { x: r2X + 80 + nodeSpacingX, y: r2Y + 80 };
    positions['S2C'] = { x: r2X + 80 + nodeSpacingX, y: r2Y + 180 }; // Challenge below
    positions['S3'] = { x: r2X + 80 + nodeSpacingX * 2, y: r2Y + 100 };
    positions['S4'] = { x: r2X + 80 + nodeSpacingX * 3, y: r2Y + 140 }; // Gate
    
    // Region 3 - Ruins (Bottom Left)
    const r3X = mapLeft;
    const r3Y = mapTop + regionHeight;
    positions['R1'] = { x: r3X + 80, y: r3Y + 120 };
    positions['R2'] = { x: r3X + 80 + nodeSpacingX, y: r3Y + 80 };
    positions['R2C'] = { x: r3X + 80 + nodeSpacingX, y: r3Y + 180 }; // Challenge below
    positions['R3'] = { x: r3X + 80 + nodeSpacingX * 2, y: r3Y + 100 };
    positions['R4'] = { x: r3X + 80 + nodeSpacingX * 3, y: r3Y + 140 }; // Gate
    
    // Region 4 - Volcano (Bottom Right)
    const r4X = mapLeft + regionWidth;
    const r4Y = mapTop + regionHeight;
    positions['V1'] = { x: r4X + 80, y: r4Y + 120 };
    positions['V2'] = { x: r4X + 80 + nodeSpacingX, y: r4Y + 80 };
    positions['V2C'] = { x: r4X + 80 + nodeSpacingX, y: r4Y + 180 }; // Challenge below
    positions['V3'] = { x: r4X + 80 + nodeSpacingX * 2, y: r4Y + 100 };
    positions['V4'] = { x: r4X + 80 + nodeSpacingX * 3, y: r4Y + 140 }; // Final
    
    return positions;
  }
  
  private drawRegionLabels(): void {
    const mapLeft = MAP_LEFT;
    const mapTop = MAP_TOP;
    const regionWidth = MAP_REGION_WIDTH;
    const regionHeight = MAP_REGION_HEIGHT;
    const boxWidth = 480;
    const boxHeight = 250;
    
    const graphics = this.add.graphics();
    
    // Region 1 - Forest (Top Left)
    graphics.fillStyle(REGION_COLORS[1], 0.2);
    graphics.fillRoundedRect(mapLeft, mapTop, boxWidth, boxHeight, 16);
    this.add.text(mapLeft + 15, mapTop + 10, '🌿 ' + REGION_NAMES[1], {
      fontSize: '16px', color: '#88cc88', fontStyle: 'bold'
    });
    
    // Region 2 - Swamp (Top Right)
    graphics.fillStyle(REGION_COLORS[2], 0.2);
    graphics.fillRoundedRect(mapLeft + regionWidth, mapTop, boxWidth, boxHeight, 16);
    this.add.text(mapLeft + regionWidth + 15, mapTop + 10, '🌾 ' + REGION_NAMES[2], {
      fontSize: '16px', color: '#99aa77', fontStyle: 'bold'
    });
    
    // Region 3 - Ruins (Bottom Left)
    graphics.fillStyle(REGION_COLORS[3], 0.2);
    graphics.fillRoundedRect(mapLeft, mapTop + regionHeight, boxWidth, boxHeight, 16);
    this.add.text(mapLeft + 15, mapTop + regionHeight + 10, '🪨 ' + REGION_NAMES[3], {
      fontSize: '16px', color: '#ccaa88', fontStyle: 'bold'
    });
    
    // Region 4 - Volcano (Bottom Right)
    graphics.fillStyle(REGION_COLORS[4], 0.2);
    graphics.fillRoundedRect(mapLeft + regionWidth, mapTop + regionHeight, boxWidth, boxHeight, 16);
    this.add.text(mapLeft + regionWidth + 15, mapTop + regionHeight + 10, '🌋 ' + REGION_NAMES[4], {
      fontSize: '16px', color: '#ff8844', fontStyle: 'bold'
    });
    
    graphics.setDepth(-2);
  }

  private createNodeVisual(
    x: number,
    y: number,
    status: { unlocked: boolean; completed: boolean; stars: number },
    def?: { regionNumber?: number; isChallenge?: boolean; isGate?: boolean; id?: string }
  ): Phaser.GameObjects.Container {
    const container = this.add.container(x, y);
    
    const region = def?.regionNumber ?? 1;
    const isChallenge = def?.isChallenge ?? false;
    const isGate = def?.isGate ?? false;
    
    // Node size - smaller for challenges
    const nodeSize = isChallenge ? 25 : 30;

    // Node color based on state and region (completed = same as unlocked, no green fill)
    let color: number;
    let alpha: number = 1;

    if (!status.unlocked) {
      color = COLORS.VOLCANIC_CHARCOAL;
      alpha = 0.5;
    } else {
      // Use region color for all unlocked (completed or not)
      color = REGION_COLORS[region] ?? COLORS.PRIMARY;
    }

    // Different shapes for different node types
    if (isGate) {
      // Gate nodes are hexagons (stars)
      const star = this.add.star(0, 0, 6, nodeSize - 8, nodeSize, color);
      star.setStrokeStyle(2, status.unlocked ? COLORS.NEON_ICE : COLORS.DEEP_AMBER);
      star.setAlpha(alpha);
      container.add(star);
    } else if (isChallenge) {
      // Challenge nodes are diamonds
      const diamond = this.add.polygon(0, 0, [
        { x: 0, y: -nodeSize }, { x: nodeSize, y: 0 },
        { x: 0, y: nodeSize }, { x: -nodeSize, y: 0 }
      ], color);
      diamond.setStrokeStyle(2, status.unlocked ? COLORS.PRIMARY : COLORS.DEEP_AMBER);
      diamond.setAlpha(alpha);
      container.add(diamond);
    } else {
      // Regular nodes are circles
      const circle = this.add.circle(0, 0, nodeSize, color);
      circle.setStrokeStyle(2, status.unlocked ? COLORS.NEON_ICE : COLORS.DEEP_AMBER);
      circle.setAlpha(alpha);
      container.add(circle);
    }

    // Stars above all unlocked nodes (over the level)
    if (status.unlocked) {
      const starsStr = '★'.repeat(status.stars) + '☆'.repeat(3 - status.stars);
      const starsText = this.add.text(0, -nodeSize - 14, starsStr, {
        fontSize: isChallenge ? '10px' : '12px',
        color: COLORS_HEX.TIKI_GOLD,
      });
      starsText.setOrigin(0.5);
      container.add(starsText);
    }

    // Locked icon or level indicator inside node
    if (!status.unlocked) {
      const lockText = this.add.text(0, 0, '🔒', {
        fontSize: isChallenge ? '14px' : '18px',
      });
      lockText.setOrigin(0.5);
      lockText.setAlpha(0.7);
      container.add(lockText);
    } else {
      if (status.completed) {
        const checkText = this.add.text(0, 0, '✓', {
          fontSize: isChallenge ? '18px' : '22px',
          color: '#50c878',
          fontStyle: 'bold',
        });
        checkText.setOrigin(0.5);
        container.add(checkText);
      } else {
        const idText = this.add.text(0, 0, def?.id ?? '', {
          fontSize: isChallenge ? '10px' : '12px',
          color: '#ffffff',
          fontStyle: 'bold',
        });
        idText.setOrigin(0.5);
        container.add(idText);
      }
    }

    // Special indicators
    if (isGate && status.unlocked) {
      const gateIcon = this.add.text(0, nodeSize + 10, '⬇', {
        fontSize: '14px', color: COLORS_HEX.TIKI_GOLD
      });
      gateIcon.setOrigin(0.5);
      container.add(gateIcon);
    }
    
    if (isChallenge && status.unlocked) {
      const challengeIcon = this.add.text(0, -nodeSize - 8, '⚡', {
        fontSize: '12px'
      });
      challengeIcon.setOrigin(0.5);
      container.add(challengeIcon);
    }

    return container;
  }

  private drawConnections(): void {
    const graphics = this.add.graphics();
    
    // Create a map for quick node lookup
    const nodeMap = new Map<string, MapNode>();
    this.nodes.forEach(node => nodeMap.set(node.scenarioId, node));
    
    // Draw connections based on each node's connections array
    const drawnConnections = new Set<string>();
    
    this.nodes.forEach(fromNode => {
      fromNode.connections.forEach(toId => {
        const toNode = nodeMap.get(toId);
        if (!toNode) return;
        
        // Avoid drawing same connection twice
        const connKey = [fromNode.scenarioId, toId].sort().join('-');
        if (drawnConnections.has(connKey)) return;
        drawnConnections.add(connKey);
        
        // Style based on unlock state
        if (!toNode.unlocked) {
          // Dashed line for locked (Deep Amber - outline)
          graphics.lineStyle(2, COLORS.DEEP_AMBER, 0.4);
        } else if (fromNode.completed) {
          // Bright line from completed nodes (Glitch Cyan - special)
          graphics.lineStyle(3, COLORS.GLITCH_CYAN, 0.8);
        } else {
          // Normal line
          graphics.lineStyle(2, COLORS.BG_LIGHT, 0.6);
        }
        
        graphics.beginPath();
        graphics.moveTo(fromNode.x, fromNode.y);
        graphics.lineTo(toNode.x, toNode.y);
        graphics.strokePath();
      });
    });
    
    // Draw cross-region connections (gate to next region's first node)
    // F4 -> S1 (right), S4 -> R1 (diagonal down-left), R4 -> V1 (right)
    const gateConnections = [
      { from: 'F4', to: 'S1' },
      { from: 'S4', to: 'R1' },
      { from: 'R4', to: 'V1' },
    ];
    
    gateConnections.forEach(({ from, to }) => {
      const fromNode = nodeMap.get(from);
      const toNode = nodeMap.get(to);
      if (!fromNode || !toNode) return;
      
      if (toNode.unlocked) {
        graphics.lineStyle(4, COLORS.PRIMARY, 0.9);
      } else {
        graphics.lineStyle(3, COLORS.DEEP_AMBER, 0.5);
      }
      
      // Draw curved arrow connection
      graphics.beginPath();
      graphics.moveTo(fromNode.x + 25, fromNode.y);
      
      // Curve through midpoint
      const midX = (fromNode.x + toNode.x) / 2;
      const midY = (fromNode.y + toNode.y) / 2;
      
      graphics.lineTo(midX, midY);
      graphics.lineTo(toNode.x - 25, toNode.y);
      graphics.strokePath();
      
      // Draw arrow head (Tiki Gold / Deep Amber)
      if (toNode.unlocked) {
        graphics.fillStyle(COLORS.PRIMARY, 0.9);
      } else {
        graphics.fillStyle(COLORS.DEEP_AMBER, 0.5);
      }
      graphics.fillTriangle(
        toNode.x - 25, toNode.y,
        toNode.x - 40, toNode.y - 8,
        toNode.x - 40, toNode.y + 8
      );
    });

    // Send to back
    graphics.setDepth(-1);
  }

  private createInfoPanel(): void {
    // Position info panel at bottom center — taller panel with clear two-column layout
    const panelX = DESIGN_WIDTH / 2;
    const panelY = DESIGN_HEIGHT - 150;
    const panelWidth = 820;
    const panelHeight = 300;

    // Create panel background (Volcanic Charcoal frame, Tiki Gold border)
    const panelBg = this.add.rectangle(panelX, panelY, panelWidth, panelHeight, COLORS.BG_DARK, 0.95);
    panelBg.setStrokeStyle(3, COLORS.PRIMARY);
    panelBg.setDepth(5);

    // Vertical divider between left (description + your stats) and right (difficulty, stars, status, towers)
    const dividerX = panelX;
    const dividerTop = panelY - panelHeight / 2 + 70;
    const dividerBottom = panelY + panelHeight / 2 - 24;
    const dividerGraphics = this.add.graphics();
    dividerGraphics.lineStyle(1, COLORS.PRIMARY, 0.35);
    dividerGraphics.lineBetween(dividerX, dividerTop, dividerX, dividerBottom);
    dividerGraphics.setDepth(6);

    // Column positions: left column ends before center; right column starts well right of center
    const leftCol = panelX - panelWidth / 2 + 44;
    const rightCol = panelX + 52;
    const leftColMaxWidth = dividerX - leftCol - 24;  // space before divider
    const topY = panelY - panelHeight / 2 + 32;

    // Level name — centered at top with margin below
    this.levelNameText = this.add.text(panelX, topY, '', {
      fontSize: '28px',
      color: COLORS_HEX.NEON_ICE,
      fontStyle: 'bold',
    });
    this.levelNameText.setOrigin(0.5, 0);
    this.levelNameText.setDepth(10);

    // ----- Left column: region, description, your stats -----
    const leftBlockStart = topY + 52;
    this.levelDescText = this.add.text(leftCol, leftBlockStart, '', {
      fontSize: '17px',
      color: COLORS_HEX.TEXT_SECONDARY,
      wordWrap: { width: leftColMaxWidth },
      lineSpacing: 8,
    });
    this.levelDescText.setOrigin(0, 0);
    this.levelDescText.setDepth(10);

    const statsBlockStart = leftBlockStart + 92;  // below description with gap
    this.levelScoreText = this.add.text(leftCol, statsBlockStart, '', {
      fontSize: '18px',
      color: COLORS_HEX.NEON_ICE,
    });
    this.levelScoreText.setOrigin(0, 0);
    this.levelScoreText.setDepth(10);

    this.levelTimeText = this.add.text(leftCol, statsBlockStart + 32, '', {
      fontSize: '18px',
      color: COLORS_HEX.NEON_ICE,
    });
    this.levelTimeText.setOrigin(0, 0);
    this.levelTimeText.setDepth(10);

    this.levelGlobalBestText = this.add.text(leftCol, statsBlockStart + 64, '', {
      fontSize: '14px',
      color: COLORS_HEX.TEXT_MUTED,
    });
    this.levelGlobalBestText.setOrigin(0, 0);
    this.levelGlobalBestText.setDepth(10);

    // ----- Right column: difficulty, stars, status, tower stats -----
    const rightBlockStart = topY + 52;
    this.levelDifficultyText = this.add.text(rightCol, rightBlockStart, '', {
      fontSize: '17px',
      color: COLORS_HEX.NEON_ICE,
    });
    this.levelDifficultyText.setOrigin(0, 0);
    this.levelDifficultyText.setDepth(10);

    this.levelStarsText = this.add.text(rightCol, rightBlockStart + 42, '', {
      fontSize: '28px',
      color: COLORS_HEX.TIKI_GOLD,
    });
    this.levelStarsText.setOrigin(0, 0);
    this.levelStarsText.setDepth(10);

    this.statusText = this.add.text(rightCol, rightBlockStart + 88, '', {
      fontSize: '18px',
      fontStyle: 'bold',
    });
    this.statusText.setOrigin(0, 0);
    this.statusText.setDepth(10);

    this.towerStatsText = this.add.text(rightCol, rightBlockStart + 132, '', {
      fontSize: '13px',
      color: COLORS_HEX.TEXT_MUTED,
      lineSpacing: 6,
    });
    this.towerStatsText.setOrigin(0, 0);
    this.towerStatsText.setDepth(10);

    // Store panel reference (destroy bg and divider)
    this.infoPanel = {
      destroy: () => {
        panelBg.destroy();
        dividerGraphics.destroy();
      },
    } as UIPanel;
  }

  private createButtons(): void {
    const panelX = DESIGN_WIDTH / 2;
    const panelY = DESIGN_HEIGHT - 150;
    const panelWidth = 820;

    // Start button - left side of panel
    this.startButton = new UIButton(this, {
      x: panelX - panelWidth / 2 - 110,
      y: panelY,
      width: 160,
      text: 'START LEVEL',
      onClick: () => this.startLevel(),
    });

    // Back button - right side of panel
    this.backButton = new UIButton(this, {
      x: panelX + panelWidth / 2 + 110,
      y: panelY,
      width: 160,
      text: 'BACK TO MENU',
      onClick: () => SceneRouter.goToMenu(SCENES.MAP),
    });

    // Right column: Tutorial, Experiment, Map Editor — equally spaced with margin from top/side
    const rightColX = DESIGN_WIDTH - 140;
    const rightColTopMargin = 90;
    const rightColButtonSpacing = 58;
    this.tutorialButton = new UIButton(this, {
      x: rightColX,
      y: rightColTopMargin,
      text: 'Tutorial',
      onClick: () => this.startTutorial(),
    });
    this.experimentButton = new UIButton(this, {
      x: rightColX,
      y: rightColTopMargin + rightColButtonSpacing,
      text: 'EXPERIMENT',
      onClick: () => this.startExperimentMode(),
    });
    this.mapEditorButton = new UIButton(this, {
      x: rightColX,
      y: rightColTopMargin + rightColButtonSpacing * 2,
      text: 'MAP EDITOR',
      onClick: () => this.openMapEditor(),
    });
  }

  private isFocusOnMap(): boolean {
    return this.selectedIndex < this.nodes.length;
  }

  private getButtonFocus(): number {
    return this.selectedIndex - this.nodes.length;
  }

  private openMapEditor(): void {
    this.scene.start('MapEditorScene');
  }

  private startTutorial(): void {
    GameState.setSelectedScenario('Tutorial');
    SceneRouter.goToGameplay(SCENES.MAP, 'Tutorial');
  }

  private startExperimentMode(): void {
    GameState.startExperimentMode();
    GameState.setSelectedScenario('F1'); // Use F1 as base map
    SceneRouter.goToGameplay(SCENES.MAP, 'F1');
  }

  private selectFirstAvailable(): void {
    // Find first unlocked but not completed, or first unlocked
    const firstUnlocked = this.nodes.findIndex(n => n.unlocked && !n.completed);
    if (firstUnlocked >= 0) {
      this.selectedIndex = firstUnlocked;
    } else {
      const anyUnlocked = this.nodes.findIndex(n => n.unlocked);
      this.selectedIndex = anyUnlocked >= 0 ? anyUnlocked : 0;
    }
  }

  private updateSelection(): void {
    const onMap = this.isFocusOnMap();

    // Update selection dancer position and visibility (only when focus is on map)
    if (this.mapSelectorDancer && this.mapSelectorDancer.scene === this) {
      this.mapSelectorDancer.setVisible(onMap);
      if (onMap) {
        const node = this.nodes[this.selectedIndex];
        if (node) {
          this.mapSelectorDancer.setPosition(node.x, node.y - 18);
        }
      }
    }

    // Update button highlight (when focus is on a button)
    this.tutorialButton.setSelected(onMap ? false : this.getButtonFocus() === FOCUS_TUTORIAL);
    this.experimentButton.setSelected(onMap ? false : this.getButtonFocus() === FOCUS_EXPERIMENT);
    this.mapEditorButton.setSelected(onMap ? false : this.getButtonFocus() === FOCUS_MAP_EDITOR);
    this.startButton.setSelected(onMap ? false : this.getButtonFocus() === FOCUS_START);
    this.backButton.setSelected(onMap ? false : this.getButtonFocus() === FOCUS_BACK);

    if (onMap) {
      this.lastNodeIndex = this.selectedIndex;
    }
    // Update info panel from selected node (when on map) or last selected node (when on button)
    const selected = this.nodes[this.lastNodeIndex];
    if (selected) {
      // Name with special indicator
      let namePrefix = '';
      if (selected.isChallenge) namePrefix = '⚡ ';
      if (selected.isGate) namePrefix = '🚪 ';
      this.levelNameText.setText(namePrefix + selected.name);
      
      // Description with region
      const regionName = REGION_NAMES[selected.region] || '';
      this.levelDescText.setText(`${regionName}\n\n${selected.description}`);

      // Difficulty as skulls
      const difficultyStr = '💀'.repeat(selected.difficulty) + '○'.repeat(5 - selected.difficulty);
      this.levelDifficultyText.setText(`Difficulty: ${difficultyStr}`);

      if (selected.unlocked) {
        if (selected.completed) {
          const starsStr = '★'.repeat(selected.stars) + '☆'.repeat(3 - selected.stars);
          this.levelStarsText.setText(starsStr);
          const pLabel = selected.bestPlayerCount > 1 ? ` (${selected.bestPlayerCount}P)` : '';
          this.levelScoreText.setText(`Your best: ${formatNumber(selected.bestScore)}${pLabel}`);
          this.levelTimeText.setText(`Time: ${formatTime(selected.bestTime)}`);
          this.levelGlobalBestText.setText('Global best: —');
          this.statusText.setText('COMPLETED');
          this.statusText.setColor(COLORS_HEX.GLITCH_CYAN);

          // Show tower stats for completed levels
          this.showTowerStatsForLevel(selected.scenarioId);

          // Fetch global best async (no delay; update when ready)
          const scenarioId = selected.scenarioId;
          this._lastFetchedScenarioId = scenarioId;
          fetchLevelScores(scenarioId, 1).then((data) => {
            if (!this.levelGlobalBestText || !this.scene.isActive() || this._lastFetchedScenarioId !== scenarioId) return;
            if (data?.scores?.length) {
              const top = data.scores[0];
              const topPLabel = (top.player_count ?? 1) > 1 ? ` (${top.player_count}P)` : '';
              this.levelGlobalBestText.setText(`Global best: ${formatNumber(top.score)}${topPLabel} by ${top.display_name}`);
            }
          }).catch(() => {});
        } else {
          this.levelStarsText.setText('☆☆☆');
          this.levelScoreText.setText('Not yet played');
          this.levelTimeText.setText('');
          this.levelGlobalBestText.setText('Global best: —');
          this.towerStatsText.setText('');

          const scenarioId = selected.scenarioId;
          this._lastFetchedScenarioId = scenarioId;
          fetchLevelScores(scenarioId, 1).then((data) => {
            if (!this.levelGlobalBestText || !this.scene.isActive() || this._lastFetchedScenarioId !== scenarioId) return;
            if (data?.scores?.length) {
              const top = data.scores[0];
              const topPLabel = (top.player_count ?? 1) > 1 ? ` (${top.player_count}P)` : '';
              this.levelGlobalBestText.setText(`Global best: ${formatNumber(top.score)}${topPLabel} by ${top.display_name}`);
            }
          }).catch(() => {});

          if (selected.isChallenge) {
            this.statusText.setText('CHALLENGE READY');
            this.statusText.setColor(COLORS_HEX.TIKI_GOLD);
          } else if (selected.isGate) {
            this.statusText.setText('GATE - BOSS BATTLE');
            this.statusText.setColor(COLORS_HEX.DEEP_AMBER);
          } else {
            this.statusText.setText('READY');
            this.statusText.setColor(COLORS_HEX.NEON_ICE);
          }
        }
        this.startButton.setEnabled(true);
      } else {
        this.levelStarsText.setText('🔒');
        this.levelScoreText.setText('Complete previous');
        this.levelTimeText.setText('level to unlock');
        this.levelGlobalBestText.setText('');
        this.statusText.setText('LOCKED');
        this.statusText.setColor(COLORS_HEX.DANGER);
        this.towerStatsText.setText('');
        this.startButton.setEnabled(false);
      }
    }
  }

  private showTowerStatsForLevel(scenarioId: string): void {
    const levelStats = ProgressState.getLevelStats(scenarioId);
    if (!levelStats) {
      this.towerStatsText.setText('');
      return;
    }

    // Get top 3 towers by damage
    const sortedTowers = Object.values(levelStats.towersStats)
      .filter((s: TowerDamageStats) => s.totalDamage > 0)
      .sort((a: TowerDamageStats, b: TowerDamageStats) => b.totalDamage - a.totalDamage)
      .slice(0, 3);

    if (sortedTowers.length === 0) {
      this.towerStatsText.setText('No tower damage recorded');
      return;
    }

    // Format stats summary
    const lines = ['Top Towers:'];
    sortedTowers.forEach((stat: TowerDamageStats) => {
      const dmg = this.formatDamageShort(stat.totalDamage);
      lines.push(`  ${stat.displayName}: ${dmg} dmg (${stat.kills} kills)`);
    });

    this.towerStatsText.setText(lines.join('\n'));
  }

  private formatDamageShort(damage: number): string {
    if (damage >= 1000000) {
      return `${(damage / 1000000).toFixed(1)}M`;
    } else if (damage >= 1000) {
      return `${(damage / 1000).toFixed(1)}K`;
    }
    return damage.toString();
  }

  private findClosestNode(direction: 'up' | 'down' | 'left' | 'right'): number {
    const current = this.nodes[this.selectedIndex];
    if (!current) return this.selectedIndex;
    
    let bestIndex = this.selectedIndex;
    let bestDistance = Infinity;
    
    this.nodes.forEach((node, index) => {
      if (index === this.selectedIndex) return;
      
      const dx = node.x - current.x;
      const dy = node.y - current.y;
      
      // Check direction match
      let isValidDirection = false;
      switch (direction) {
        case 'up': isValidDirection = dy < -20; break;
        case 'down': isValidDirection = dy > 20; break;
        case 'left': isValidDirection = dx < -20; break;
        case 'right': isValidDirection = dx > 20; break;
      }
      
      if (!isValidDirection) return;
      
      // Calculate distance with direction bias
      let distance: number;
      if (direction === 'up' || direction === 'down') {
        distance = Math.abs(dy) + Math.abs(dx) * 0.5; // Bias towards vertical
      } else {
        distance = Math.abs(dx) + Math.abs(dy) * 0.5; // Bias towards horizontal
      }
      
      if (distance < bestDistance) {
        bestDistance = distance;
        bestIndex = index;
      }
    });
    
    return bestIndex;
  }

  private navigateUp(): void {
    if (this.isFocusOnMap()) {
      const newIndex = this.findClosestNode('up');
      if (newIndex !== this.selectedIndex) {
        this.selectedIndex = newIndex;
        this.updateSelection();
      }
      return;
    }
    const btn = this.getButtonFocus();
    if (btn === FOCUS_BACK) {
      this.selectedIndex = this.nodes.length + FOCUS_START;
      this.updateSelection();
    } else if (btn === FOCUS_START) {
      this.selectedIndex = this.nodes.length + FOCUS_MAP_EDITOR;
      this.updateSelection();
    } else if (btn === FOCUS_MAP_EDITOR) {
      this.selectedIndex = this.nodes.length + FOCUS_EXPERIMENT;
      this.updateSelection();
    } else if (btn === FOCUS_EXPERIMENT) {
      this.selectedIndex = this.nodes.length + FOCUS_TUTORIAL;
      this.updateSelection();
    } else if (btn === FOCUS_TUTORIAL) {
      this.selectedIndex = this.lastNodeIndex;
      this.updateSelection();
    }
  }

  private navigateDown(): void {
    if (this.isFocusOnMap()) {
      const newIndex = this.findClosestNode('down');
      if (newIndex !== this.selectedIndex) {
        this.selectedIndex = newIndex;
        this.updateSelection();
        return;
      }
      const current = this.nodes[this.selectedIndex];
      if (current && current.y >= MAP_TOP + MAP_REGION_HEIGHT * 0.5) {
        this.selectedIndex = this.nodes.length + FOCUS_START;
        this.updateSelection();
      }
      return;
    }
    const btn = this.getButtonFocus();
    if (btn === FOCUS_TUTORIAL) {
      this.selectedIndex = this.nodes.length + FOCUS_EXPERIMENT;
      this.updateSelection();
    } else if (btn === FOCUS_EXPERIMENT) {
      this.selectedIndex = this.nodes.length + FOCUS_MAP_EDITOR;
      this.updateSelection();
    } else if (btn === FOCUS_MAP_EDITOR) {
      this.selectedIndex = this.nodes.length + FOCUS_START;
      this.updateSelection();
    } else if (btn === FOCUS_START) {
      this.selectedIndex = this.nodes.length + FOCUS_BACK;
      this.updateSelection();
    }
  }

  private navigateLeft(): void {
    if (this.isFocusOnMap()) {
      const newIndex = this.findClosestNode('left');
      if (newIndex !== this.selectedIndex) {
        this.selectedIndex = newIndex;
        this.updateSelection();
      }
      return;
    }
    const btn = this.getButtonFocus();
    if (btn === FOCUS_EXPERIMENT) {
      this.selectedIndex = this.nodes.length + FOCUS_TUTORIAL;
      this.updateSelection();
    } else if (btn === FOCUS_MAP_EDITOR) {
      this.selectedIndex = this.nodes.length + FOCUS_EXPERIMENT;
      this.updateSelection();
    } else if (btn === FOCUS_TUTORIAL) {
      this.selectedIndex = this.lastNodeIndex;
      this.updateSelection();
    }
  }

  private navigateRight(): void {
    if (this.isFocusOnMap()) {
      const newIndex = this.findClosestNode('right');
      if (newIndex !== this.selectedIndex) {
        this.selectedIndex = newIndex;
        this.updateSelection();
        return;
      }
      const current = this.nodes[this.selectedIndex];
      if (current && current.x >= MAP_LEFT + MAP_REGION_WIDTH * 0.6) {
        this.selectedIndex = this.nodes.length + FOCUS_TUTORIAL;
        this.updateSelection();
      }
      return;
    }
    const btn = this.getButtonFocus();
    if (btn === FOCUS_TUTORIAL) {
      this.selectedIndex = this.nodes.length + FOCUS_EXPERIMENT;
      this.updateSelection();
    } else if (btn === FOCUS_EXPERIMENT) {
      this.selectedIndex = this.nodes.length + FOCUS_MAP_EDITOR;
      this.updateSelection();
    }
  }

  // ─── Account button (same pattern as MenuScene, top-left) ────────
  private createAccountButton(): void {
    this.accountContainer = this.add.container(20, 20);
    this.accountContainer.setDepth(DEPTH.UI_ELEMENTS);
    this._updateAccountButton();
    this.authUnsubscribe = AuthService.onChange(() => {
      this._updateAccountButton();
    });
  }

  private _updateAccountButton(): void {
    if (!this.accountContainer) return;
    this.accountContainer.removeAll(true);
    this.accountProfileImage = undefined;
    this.accountText = undefined;
    this.loginDancerSprite = null;

    const user = AuthService.user;
    const avatarSize = 36;

    if (user) {
      // Profile picture background circle
      const circleBg = this.add.circle(avatarSize / 2, avatarSize / 2, avatarSize / 2 + 2, COLORS.PRIMARY);
      this.accountContainer.add(circleBg);

      if (user.picture_url) {
        const textureKey = `profile-pic-${user.id}`;
        if (this.textures.exists(textureKey)) {
          this._showProfileImage(textureKey, avatarSize);
        } else {
          this._showProfileInitial(user.display_name || user.email, avatarSize);
          this.load.image(textureKey, user.picture_url);
          this.load.once('complete', () => {
            if (this.accountContainer && this.textures.exists(textureKey)) {
              if (this.accountText) { this.accountText.destroy(); this.accountText = undefined; }
              this._showProfileImage(textureKey, avatarSize);
            }
          });
          this.load.start();
        }
      } else {
        this._showProfileInitial(user.display_name || user.email, avatarSize);
      }

      const nameText = this.add.text(avatarSize + 10, avatarSize / 2, user.display_name || 'Set Name', {
        fontSize: '14px',
        color: user.display_name ? '#ffffff' : COLORS_HEX.TIKI_GOLD,
        fontStyle: 'bold',
      });
      nameText.setOrigin(0, 0.5);
      this.accountContainer.add(nameText);

      const totalWidth = avatarSize + 10 + nameText.width;
      const hitArea = this.add.rectangle(totalWidth / 2, avatarSize / 2, totalWidth + 8, avatarSize + 8, 0x000000, 0);
      hitArea.setInteractive({ useHandCursor: true });
      hitArea.on('pointerover', () => nameText.setColor(COLORS_HEX.NEON_ICE));
      hitArea.on('pointerout', () => nameText.setColor(user.display_name ? '#ffffff' : COLORS_HEX.TIKI_GOLD));
      hitArea.on('pointerdown', () => { AuthUI.showProfile(() => this._updateAccountButton()); });
      this.accountContainer.add(hitArea);

    } else {
      const iconSize = avatarSize;
      const danceKeys = ['player-1-dance-1', 'player-1-dance-2', 'player-1-dance-3', 'player-1-dance-4'];
      const hasDance = danceKeys.every((k) => this.textures.exists(k));

      if (hasDance) {
        this.loginDancerSprite = this.add.image(iconSize / 2, iconSize / 2, 'player-1-dance-1');
        this.loginDancerSprite.setDisplaySize(iconSize, iconSize);
        this.accountContainer.add(this.loginDancerSprite);
        this.loginDancerFrameIndex = 0;
        this.loginDancerTimer = 0;
        this.loginDancerCycleCount = 0;
        this.loginDancerFlipped = false;
      }

      const text = this.add.text(iconSize + 10, iconSize / 2, 'Login', {
        fontSize: '14px',
        color: COLORS_HEX.TEXT_SECONDARY,
      });
      text.setOrigin(0, 0.5);
      this.accountContainer.add(text);

      const totalWidth = iconSize + 10 + text.width;
      const hitArea = this.add.rectangle(totalWidth / 2, iconSize / 2, totalWidth + 8, iconSize + 8, 0x000000, 0);
      hitArea.setInteractive({ useHandCursor: true });
      hitArea.on('pointerover', () => text.setColor(COLORS_HEX.TIKI_GOLD));
      hitArea.on('pointerout', () => text.setColor(COLORS_HEX.TEXT_SECONDARY));
      hitArea.on('pointerdown', () => { AuthUI.showSignIn(() => this._updateAccountButton()); });
      this.accountContainer.add(hitArea);
    }
  }

  private _showProfileImage(textureKey: string, size: number): void {
    if (!this.accountContainer) return;
    const cx = size / 2;
    const img = this.add.image(cx, size / 2, textureKey);
    img.setDisplaySize(size, size);
    const maskShape = this.add.graphics();
    maskShape.fillCircle(cx, size / 2, size / 2);
    maskShape.setVisible(false);
    img.setMask(maskShape.createGeometryMask());
    this.accountContainer.add(img);
    this.accountProfileImage = img;
  }

  private _showProfileInitial(name: string, size: number): void {
    if (!this.accountContainer) return;
    const cx = size / 2;
    const initial = (name || '?')[0].toUpperCase();
    const bg = this.add.circle(cx, size / 2, size / 2, COLORS.BG_LIGHT);
    this.accountContainer.add(bg);
    const text = this.add.text(cx, size / 2, initial, {
      fontSize: '18px', color: COLORS_HEX.TIKI_GOLD, fontStyle: 'bold',
    });
    text.setOrigin(0.5);
    this.accountContainer.add(text);
    this.accountText = text;
  }

  private startLevel(): void {
    const selected = this.nodes[this.selectedIndex];
    if (!selected || !selected.unlocked) return;

    // Ensure experiment mode is off when starting a normal level from the map
    GameState.endExperimentMode();
    GameState.setSelectedScenario(selected.scenarioId);
    SceneRouter.goToGameplay(SCENES.MAP, selected.scenarioId);
  }

  /** Run chase + dancer animations (called even when auth modal is open) */
  private _updateMapAnimations(delta: number): void {
    // Chase animation frame stepping (guard: sprites exist and weren't destroyed by scene transition)
    const chasePlayerOk = this.chasePlayerSprite && this.chasePlayerSprite.scene === this;
    const chaseMonsterOk = this.chaseMonsterSprite && this.chaseMonsterSprite.scene === this;
    if (chasePlayerOk && chaseMonsterOk) {
      this.chasePlayerFrameTimer += delta;
      if (this.chasePlayerFrameTimer >= CHASE_PLAYER_FRAME_MS) {
        this.chasePlayerFrameTimer = 0;
        this.chasePlayerFrameIndex = (this.chasePlayerFrameIndex + 1) % this.chasePlayerRunKeys.length;
        this.chasePlayerSprite!.setTexture(this.chasePlayerRunKeys[this.chasePlayerFrameIndex]);
      }
      this.chaseMonsterFrameTimer += delta;
      if (this.chaseMonsterFrameTimer >= CHASE_MONSTER_FRAME_MS) {
        this.chaseMonsterFrameTimer = 0;
        this.chaseMonsterFrameIndex = (this.chaseMonsterFrameIndex + 1) % this.chaseMonsterFrameCount;
        const key = `monster-${this.chaseMonsterType}-${this.chaseMonsterFrameIndex + 1}`;
        if (this.textures.exists(key)) this.chaseMonsterSprite!.setTexture(key);
      }
      const centerX = DESIGN_WIDTH / 2;
      const halfWidth = DESIGN_WIDTH / 2;
      for (const sprite of [this.chasePlayerSprite!, this.chaseMonsterSprite!]) {
        const t = (sprite.x - centerX) / halfWidth;
        const brightness = Math.max(0.08, 1 - t * t);
        sprite.setAlpha(brightness);
      }
    }

    // Map selection dancer: BPM-synced dance
    if (this.mapSelectorDancer && this.mapSelectorDancer.scene === this && this.mapSelectorDancer.visible) {
      const bpm = AudioRouter.getCurrentBPM();
      const beatMs = 60000 / bpm;
      const frameMs = beatMs / MapScene.MAP_DANCE_FRAMES_PER_CYCLE;
      this.mapDanceTimer += delta;
      if (this.mapDanceTimer >= frameMs) {
        this.mapDanceTimer -= frameMs;
        this.mapDanceFrameIndex++;
        if (this.mapDanceFrameIndex >= MapScene.MAP_DANCE_FRAMES_PER_CYCLE) {
          this.mapDanceFrameIndex = 0;
          this.mapDanceCycleCount++;
          if (this.mapDanceCycleCount >= MapScene.MAP_DANCE_CYCLES_BEFORE_FLIP) {
            this.mapDanceCycleCount = 0;
            this.mapDanceFlipped = !this.mapDanceFlipped;
            this.mapSelectorDancer.setFlipX(this.mapDanceFlipped);
          }
        }
        const frameKey = this.mapDanceKeys[this.mapDanceFrameIndex];
        if (this.textures.exists(frameKey)) {
          this.mapSelectorDancer.setTexture(frameKey);
        }
      }
    }

    // Login button dancer: BPM-synced dance
    if (this.loginDancerSprite && this.loginDancerSprite.scene === this) {
      const bpm = AudioRouter.getCurrentBPM();
      const beatMs = 60000 / bpm;
      const frameMs = beatMs / MapScene.LOGIN_DANCE_FRAMES_PER_CYCLE;
      this.loginDancerTimer += delta;
      if (this.loginDancerTimer >= frameMs) {
        this.loginDancerTimer -= frameMs;
        this.loginDancerFrameIndex++;
        if (this.loginDancerFrameIndex >= MapScene.LOGIN_DANCE_FRAMES_PER_CYCLE) {
          this.loginDancerFrameIndex = 0;
          this.loginDancerCycleCount++;
          if (this.loginDancerCycleCount >= MapScene.LOGIN_DANCE_CYCLES_BEFORE_FLIP) {
            this.loginDancerCycleCount = 0;
            this.loginDancerFlipped = !this.loginDancerFlipped;
            this.loginDancerSprite.setFlipX(this.loginDancerFlipped);
          }
        }
        const frameKey = `player-1-dance-${this.loginDancerFrameIndex + 1}`;
        if (this.textures.exists(frameKey)) this.loginDancerSprite.setTexture(frameKey);
      }
    }
  }

  update(_time: number, delta: number): void {
    InputRouter.update();

    // When auth overlay (HTML modal) is open, block ALL game input but keep animations going
    if (AuthUI.isVisible) {
      this._updateMapAnimations(delta);
      InputRouter.lateUpdate();
      return;
    }

    this._updateMapAnimations(delta);

    // Navigation
    if (InputRouter.anyPlayerJustPressed('MOVE_UP')) {
      this.navigateUp();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_DOWN')) {
      this.navigateDown();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_LEFT')) {
      this.navigateLeft();
    }
    if (InputRouter.anyPlayerJustPressed('MOVE_RIGHT')) {
      this.navigateRight();
    }

    // Confirm (activate focused node or button)
    if (InputRouter.anyPlayerJustPressed('INTERACT') || InputRouter.anyPlayerJustPressed('START')) {
      if (this.isFocusOnMap()) {
        this.startLevel();
      } else {
        const btn = this.getButtonFocus();
        if (btn === FOCUS_TUTORIAL) this.startTutorial();
        else if (btn === FOCUS_EXPERIMENT) this.startExperimentMode();
        else if (btn === FOCUS_MAP_EDITOR) this.openMapEditor();
        else if (btn === FOCUS_START) this.startLevel();
        else if (btn === FOCUS_BACK) SceneRouter.goToMenu(SCENES.MAP);
      }
    }

    // Cancel/Back
    if (InputRouter.anyPlayerJustPressed('CANCEL')) {
      SceneRouter.goToMenu(SCENES.MAP);
    }

    // Must call lateUpdate at end of frame to properly track "just pressed" state
    InputRouter.lateUpdate();
  }
}
